import { Button } from "components/core/basic/Button";
import { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { css } from "../../../styled-system/css";
import { Flex } from "../../../styled-system/jsx";
import Tags from "components/core/blocks/Tags";
import ToolTips from "utils/toolTips";
import { useAdPlanTags } from "./hooks/useAdPlanTags";
import { HoverInfo } from "components/core/basic/Tooltip";
import { Select } from "components/fields/base/Select";

interface Query {
  subscribed: boolean;
  frequency: string;
  parameters: Partial<FormData>;
  parameter_list: (keyof FormData)[];
  description?: string;
}

interface FormData {
  days: number;
  frequency: string;
  tag: string[];
}

interface QueryFormProps {
  setShowModal: (val: boolean) => void;
  query: Query;
  onFormSubmit: (formData: FormData) => void;
}

const QUERY_LOOKBACK_PERIOD_OPTIONS = [
  { value: 7, label: "7 Days" },
  { value: 14, label: "14 Days" },
  { value: 30, label: "30 Days" },
  { value: 60, label: "60 Days" },
  { value: 90, label: "90 Days" },
] as const;

const QUERY_FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
] as const;

const getInitialFormData = (query?: Query): FormData => {
  if (!query) {
    return {
      days: QUERY_LOOKBACK_PERIOD_OPTIONS[2].value,
      frequency: QUERY_FREQUENCY_OPTIONS[1].value,
      tag: [],
    };
  }
  const initialData: FormData = {
    days: query.subscribed ? (query.parameters.days ?? 30) : QUERY_LOOKBACK_PERIOD_OPTIONS[2].value,
    frequency: query.subscribed ? query.frequency : QUERY_FREQUENCY_OPTIONS[1].value,

    tag: query.subscribed ? (query.parameters.tag ?? []) : [],
  };

  return initialData;
};

const QueryForm = ({ setShowModal, query, onFormSubmit }: QueryFormProps) => {
  const [formData, setFormData] = useState<FormData>(getInitialFormData(query));

  const queryParametersToRender = query.parameter_list;
  const { description } = query;

  const handleSubmit = () => {
    onFormSubmit(formData);
    setShowModal(false);
  };

  const { data: tagOptions } = useAdPlanTags(query.parameter_list.includes("tag"));

  return (
    <Row>
      <Col xs={12} lg={{ span: 8, offset: 2 }}>
        {description && <p className="text-center fs-standard">{description}</p>}
        <form onSubmit={(e) => e.preventDefault()}>
          <Row className="py-3">
            <Col className="px-5 py-3" xs={12} sm={{ span: 6, offset: queryParametersToRender.length ? 0 : 3 }}>
              <Select
                className={css({ margin: "medium" })}
                label={
                  <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                    Frequency{" "}
                    <HoverInfo iconClassName={css({ color: "gray", fontSize: "small" })}>
                      Daily: Gets A Report Every Day <br />
                      Weekly: Gets a Report Every Monday <br />
                      Monthly: Gets a Report Every First Day of the Month
                    </HoverInfo>
                  </span>
                }
                items={QUERY_FREQUENCY_OPTIONS}
                selectedKey={formData.frequency}
                onSelectionChange={(key) => {
                  if (key) setFormData({ ...formData, frequency: key as string });
                }}
              />
            </Col>

            {queryParametersToRender.includes("days") && (
              <Col className="px-5 py-3" xs={12} sm={6}>
                <Select
                  className={css({ margin: "medium" })}
                  label={
                    <span className={css({ display: "flex", gap: "small", alignItems: "center" })}>
                      Lookback Period{" "}
                    </span>
                  }
                  items={QUERY_LOOKBACK_PERIOD_OPTIONS}
                  selectedKey={formData.days}
                  onSelectionChange={(key) => {
                    if (typeof key === "number") {
                      setFormData({ ...formData, days: key });
                    }
                  }}
                />
              </Col>
            )}

            {queryParametersToRender.includes("tag") && (
              <Col className="px-5 py-5" xs={12}>
                <Tags
                  name={
                    <span style={{ fontSize: "1.5rem", position: "relative" }} className="pb-3 d-block fw-bold">
                      Media Plan Tags (optional)
                      <ToolTips
                        toolTip={
                          "Refine the reports generated from this subscription by limiting data to campaigns with the selected tags."
                        }
                        id={"campaignTagsQueryFormTooltip"}
                        position={"top"}
                      />
                    </span>
                  }
                  filledTags={formData.tag}
                  saveFilledTags={(tags: string[]) => {
                    if (!tagOptions?.length) return;
                    setFormData({ ...formData, tag: tags });
                  }}
                  showSuggestionsOnFocus
                  placeholder={"Add tags here..."}
                  hideEditButton
                  reviewDisplay={false}
                  suggestedTags={tagOptions}
                  showSuggestedTags
                  allowNew={false}
                  fullWidth
                />
              </Col>
            )}
          </Row>
          <Flex className={css({ padding: "large" })} gap="xlarge" justifyContent="center">
            <Button variant="secondary" onPress={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onPress={handleSubmit}>{query.subscribed ? "Update" : "Subscribe"}</Button>
          </Flex>
        </form>
      </Col>
    </Row>
  );
};

export default QueryForm;
